import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "primereact/button";
import { BreadCrumb } from "primereact/breadcrumb";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { TabView, TabPanel } from "primereact/tabview";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { Badge } from "primereact/badge";
import { ProgressBar } from "primereact/progressbar";
import { Chip } from "primereact/chip";
import { Calendar } from "primereact/calendar";
import { renewalMockData, renewalCrudOperations } from "../../../services/mockData/renewalMockData";
import SvgDot from "../../../assets/icons/SvgDot";
import "./index.scss";

const LapseManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [reasonFilter, setReasonFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [winBackVisible, setWinBackVisible] = useState(false);
  const [campaignVisible, setCampaignVisible] = useState(false);
  const [lapsedPolicies, setLapsedPolicies] = useState([]);
  const [filteredPolicies, setFilteredPolicies] = useState([]);
  const [winBackCampaigns, setWinBackCampaigns] = useState(renewalMockData.winBackCampaigns);
  const [dashboardData, setDashboardData] = useState({
    totalLapsed: 0,
    inGracePeriod: 0,
    winBackEligible: 0,
    revenueAtRisk: 0
  });
  const [winBackOffer, setWinBackOffer] = useState({
    discount: 20,
    additionalBenefits: [],
    paymentTerms: 'Standard',
    validUntil: new Date(Date.now() + 30*24*60*60*1000),
    message: ''
  });
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    startDate: new Date(),
    endDate: new Date(Date.now() + 30*24*60*60*1000),
    targetSegment: 'All Lapsed',
    discount: 25,
    benefits: [],
    budget: 100000
  });
  const toast = useRef(null);

  const statusOptions = [
    { label: "All Statuses", value: "All" },
    { label: "In Grace Period", value: "In Grace Period" },
    { label: "Lapsed", value: "Lapsed" },
    { label: "Win-back Attempted", value: "Win-back Attempted" },
    { label: "Reinstated", value: "Reinstated" }
  ];

  const reasonOptions = [
    { label: "All Reasons", value: "All" },
    { label: "Premium too high", value: "Premium too high" },
    { label: "Found cheaper alternative", value: "Found cheaper alternative" },
    { label: "No longer needed", value: "No longer needed" },
    { label: "Poor service", value: "Poor service" },
    { label: "Payment issues", value: "Payment issues" }
  ];

  const benefitOptions = [
    "Waived reinstatement fee",
    "Free add-on coverage",
    "Extended payment terms",
    "Loyalty rewards",
    "Premium freeze",
    "Free policy review",
    "24/7 support priority",
    "Cashback offer"
  ];

  const paymentTermsOptions = [
    { label: "Standard Terms", value: "Standard" },
    { label: "Extended (60 days)", value: "Extended" },
    { label: "Installments", value: "Installments" },
    { label: "Deferred Payment", value: "Deferred" }
  ];

  const targetSegmentOptions = [
    { label: "All Lapsed", value: "All Lapsed" },
    { label: "High Value (>100K)", value: "High Value" },
    { label: "Recent Lapse (<30 days)", value: "Recent Lapse" },
    { label: "Long-term Clients (>3 years)", value: "Long-term" },
    { label: "Price Sensitive", value: "Price Sensitive" }
  ];

  const items = [
    { label: "Renewals", url: "#" },
    { label: "Lapse Management", url: "#" }
  ];

  const home = { icon: <SvgDot />, url: "#" };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [search, statusFilter, reasonFilter, lapsedPolicies]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Get lapsed and grace period policies from renewal queue
      const gracePeriodPolicies = renewalMockData.renewalQueue
        .filter(policy => policy.status === 'In Grace Period')
        .map(policy => ({
          ...policy,
          lapseDate: null,
          daysLapsed: 0,
          premiumLost: policy.currentPremium,
          lapseReason: null,
          winBackAttempts: [],
          reinstatementEligible: true,
          reinstatementDeadline: policy.gracePeriodEnd,
          winBackStatus: 'Eligible'
        }));

      // Combine with existing lapsed policies
      const combinedData = [
        ...renewalMockData.lapsedPolicies,
        ...gracePeriodPolicies
      ];

      setLapsedPolicies(combinedData);
      calculateDashboard(combinedData);

      setTimeout(() => {
        setLoading(false);
        toast.current.show({
          severity: 'success',
          summary: 'Data Loaded',
          detail: 'Lapse management data loaded successfully',
          life: 3000
        });
      }, 1000);
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load lapse data',
        life: 3000
      });
      setLoading(false);
    }
  };

  const calculateDashboard = (data) => {
    const inGrace = data.filter(p => p.status === 'In Grace Period');
    const lapsed = data.filter(p => p.lapseDate);
    const eligible = data.filter(p => p.reinstatementEligible);
    const totalRevenue = data.reduce((sum, p) => sum + (p.premiumLost || p.currentPremium), 0);

    setDashboardData({
      totalLapsed: data.length,
      inGracePeriod: inGrace.length,
      winBackEligible: eligible.length,
      revenueAtRisk: totalRevenue
    });
  };

  const applyFilters = () => {
    let filtered = [...lapsedPolicies];

    if (search) {
      filtered = filtered.filter(p =>
        p.policyNumber.toLowerCase().includes(search.toLowerCase()) ||
        p.insuredName.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (statusFilter !== 'All') {
      if (statusFilter === 'Lapsed') {
        filtered = filtered.filter(p => p.lapseDate);
      } else if (statusFilter === 'In Grace Period') {
        filtered = filtered.filter(p => p.status === 'In Grace Period');
      } else {
        filtered = filtered.filter(p => p.winBackStatus === statusFilter);
      }
    }

    if (reasonFilter !== 'All') {
      filtered = filtered.filter(p => p.lapseReason === reasonFilter);
    }

    setFilteredPolicies(filtered);
    calculateDashboard(filtered);
  };

  const handleClear = () => {
    setSearch("");
    setStatusFilter("All");
    setReasonFilter("All");
    toast.current.show({
      severity: 'info',
      summary: 'Filters Cleared',
      detail: 'All filters have been reset',
      life: 2000
    });
  };

  const handleViewDetails = (rowData) => {
    setSelectedPolicy(rowData);
    setDetailsVisible(true);
  };

  const handleWinBack = (rowData) => {
    setSelectedPolicy(rowData);
    setWinBackOffer({
      discount: 20,
      additionalBenefits: ["Waived reinstatement fee"],
      paymentTerms: 'Standard',
      validUntil: new Date(Date.now() + 30*24*60*60*1000),
      message: ''
    });
    setWinBackVisible(true);
  };

  const handleCreateCampaign = () => {
    setNewCampaign({
      name: '',
      startDate: new Date(),
      endDate: new Date(Date.now() + 30*24*60*60*1000),
      targetSegment: 'All Lapsed',
      discount: 25,
      benefits: [],
      budget: 100000
    });
    setCampaignVisible(true);
  };

  const handleSendWinBack = async () => {
    setLoading(true);
    try {
      const result = await renewalCrudOperations.processWinBack(
        selectedPolicy.policyNumber,
        winBackOffer
      );

      if (result.success) {
        // Update policy status
        const updatedPolicies = lapsedPolicies.map(p =>
          p.policyNumber === selectedPolicy.policyNumber
            ? {
                ...p,
                winBackAttempts: [
                  ...p.winBackAttempts,
                  {
                    date: new Date().toISOString().split('T')[0],
                    method: 'Email',
                    offer: `${winBackOffer.discount}% discount`,
                    response: 'Pending'
                  }
                ],
                winBackStatus: 'In Progress'
              }
            : p
        );

        setLapsedPolicies(updatedPolicies);
        setWinBackVisible(false);

        toast.current.show({
          severity: 'success',
          summary: 'Win-back Sent',
          detail: `Win-back offer sent to ${selectedPolicy.insuredName}`,
          life: 3000
        });
      }
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to send win-back offer',
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaignConfirm = () => {
    const campaignId = `WB-2025-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`;

    const campaign = {
      ...newCampaign,
      campaignId,
      statistics: {
        targetedPolicies: 0,
        contacted: 0,
        responded: 0,
        converted: 0,
        conversionRate: 0,
        revenueRecovered: 0
      }
    };

    setWinBackCampaigns([...winBackCampaigns, campaign]);
    setCampaignVisible(false);

    toast.current.show({
      severity: 'success',
      summary: 'Campaign Created',
      detail: `Win-back campaign "${newCampaign.name}" created successfully`,
      life: 3000
    });
  };

  const statusBodyTemplate = (rowData) => {
    const status = rowData.status || (rowData.lapseDate ? 'Lapsed' : 'Active');
    const getSeverity = (status) => {
      switch (status) {
        case 'In Grace Period': return 'warning';
        case 'Lapsed': return 'danger';
        case 'Reinstated': return 'success';
        case 'Win-back Attempted': return 'info';
        default: return 'secondary';
      }
    };

    return <Tag value={status} severity={getSeverity(status)} />;
  };

  const daysLapsedBodyTemplate = (rowData) => {
    if (rowData.status === 'In Grace Period') {
      const graceDays = Math.floor((new Date(rowData.gracePeriodEnd) - new Date()) / (1000 * 60 * 60 * 24));
      return (
        <div className="grace-period-cell">
          <i className="pi pi-clock warning"></i>
          <span className="warning">{graceDays} days left</span>
        </div>
      );
    }

    if (rowData.daysLapsed) {
      const severity = rowData.daysLapsed <= 30 ? 'info' : rowData.daysLapsed <= 90 ? 'warning' : 'danger';
      return (
        <div className="lapsed-days-cell">
          <i className={`pi pi-calendar ${severity}`}></i>
          <span className={severity}>{rowData.daysLapsed} days</span>
        </div>
      );
    }

    return <span>-</span>;
  };

  const premiumBodyTemplate = (rowData) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0
    }).format(rowData.premiumLost || rowData.currentPremium);
  };

  const winBackStatusBodyTemplate = (rowData) => {
    const status = rowData.winBackStatus || 'Eligible';
    const getSeverity = (status) => {
      switch (status) {
        case 'Eligible': return 'success';
        case 'In Progress': return 'info';
        case 'Converted': return 'success';
        case 'Declined': return 'danger';
        default: return 'secondary';
      }
    };

    return <Badge value={status} severity={getSeverity(status)} />;
  };

  const attemptsBodyTemplate = (rowData) => {
    const attempts = rowData.winBackAttempts?.length || 0;
    const maxAttempts = 3;
    const percentage = (attempts / maxAttempts) * 100;

    return (
      <div className="attempts-cell">
        <ProgressBar value={percentage} style={{ width: '60px', height: '8px' }} />
        <span>{attempts}/{maxAttempts}</span>
      </div>
    );
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="action-buttons">
        <Button
          icon="pi pi-eye"
          className="p-button-text"
          onClick={() => handleViewDetails(rowData)}
          tooltip="View Details"
        />
        <Button
          icon="pi pi-send"
          className="p-button-text"
          onClick={() => handleWinBack(rowData)}
          tooltip="Send Win-back"
          disabled={!rowData.reinstatementEligible || rowData.winBackStatus === 'Converted'}
        />
      </div>
    );
  };

  const policyLinkTemplate = (rowData) => {
    return (
      <a href="#" className="policy-link" onClick={(e) => {
        e.preventDefault();
        handleViewDetails(rowData);
      }}>
        {rowData.policyNumber}
      </a>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const winBackDialogFooter = (
    <div className="dialog-footer">
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => setWinBackVisible(false)}
      />
      <Button
        label="Send Win-back"
        icon="pi pi-send"
        onClick={handleSendWinBack}
        loading={loading}
      />
    </div>
  );

  const campaignDialogFooter = (
    <div className="dialog-footer">
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => setCampaignVisible(false)}
      />
      <Button
        label="Create Campaign"
        icon="pi pi-check"
        onClick={handleCreateCampaignConfirm}
      />
    </div>
  );

  return (
    <div className="container__lapse__management__master">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="top__container">
        <h1 className="page__title">Lapse Management</h1>
        <BreadCrumb model={items} home={home} />
      </div>

      <div className="content-container">
        {/* Search Section */}
        <div className="search-section">
          <Card>
            <div className="search-grid">
              <div className="search-field">
                <label>Policy/Insured Name</label>
                <InputText
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search policies or names"
                />
              </div>

              <div className="search-field">
                <label>Status</label>
                <Dropdown
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.value)}
                  options={statusOptions}
                />
              </div>

              <div className="search-field">
                <label>Lapse Reason</label>
                <Dropdown
                  value={reasonFilter}
                  onChange={(e) => setReasonFilter(e.value)}
                  options={reasonOptions}
                />
              </div>

              <div className="search-actions">
                <Button label="Search" icon="pi pi-search" onClick={applyFilters} />
                <Button label="Clear" className="p-button-secondary" onClick={handleClear} />
              </div>
            </div>
          </Card>
        </div>

        {/* Dashboard Cards */}
        <div className="dashboard-cards">
          <Card className="dashboard-card">
            <div className="card-content">
              <i className="pi pi-ban card-icon red"></i>
              <div className="card-info">
                <span className="card-value">{dashboardData.totalLapsed}</span>
                <span className="card-label">Total At Risk</span>
              </div>
            </div>
          </Card>

          <Card className="dashboard-card clickable" onClick={() => setStatusFilter('In Grace Period')}>
            <div className="card-content">
              <i className="pi pi-clock card-icon orange"></i>
              <div className="card-info">
                <span className="card-value">{dashboardData.inGracePeriod}</span>
                <span className="card-label">In Grace Period</span>
              </div>
            </div>
          </Card>

          <Card className="dashboard-card clickable" onClick={() => setStatusFilter('Eligible')}>
            <div className="card-content">
              <i className="pi pi-refresh card-icon blue"></i>
              <div className="card-info">
                <span className="card-value">{dashboardData.winBackEligible}</span>
                <span className="card-label">Win-back Eligible</span>
              </div>
            </div>
          </Card>

          <Card className="dashboard-card">
            <div className="card-content">
              <i className="pi pi-dollar card-icon dark-red"></i>
              <div className="card-info">
                <span className="card-value">{formatCurrency(dashboardData.revenueAtRisk)}</span>
                <span className="card-label">Revenue at Risk</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content */}
        <div className="main-content">
          <TabView>
            <TabPanel header="Lapsed Policies">
              <div className="table-section">
                <Card>
                  <div className="table-header">
                    <h3>Lapsed & Grace Period Policies</h3>
                    <div className="table-actions">
                      <Button
                        label="Create Campaign"
                        icon="pi pi-plus"
                        onClick={handleCreateCampaign}
                      />
                      <Button
                        icon="pi pi-refresh"
                        className="p-button-text"
                        onClick={loadInitialData}
                        tooltip="Refresh"
                      />
                      <Button
                        icon="pi pi-file-excel"
                        className="p-button-text"
                        onClick={() => {
                          toast.current.show({
                            severity: 'success',
                            summary: 'Export Started',
                            detail: 'Lapse management data exported to Excel',
                            life: 3000
                          });
                        }}
                        tooltip="Export to Excel"
                      />
                    </div>
                  </div>

                  <DataTable
                    value={filteredPolicies.length > 0 ? filteredPolicies : lapsedPolicies}
                    className="lapse-table"
                    stripedRows
                    paginator
                    rows={10}
                    loading={loading}
                    emptyMessage="No lapsed policies found"
                    sortMode="multiple"
                  >
                    <Column
                      body={policyLinkTemplate}
                      header="Policy Number"
                      style={{ width: '12%' }}
                      sortable
                      sortField="policyNumber"
                    />
                    <Column
                      field="insuredName"
                      header="Insured Name"
                      style={{ width: '14%' }}
                      sortable
                    />
                    <Column
                      field="product"
                      header="Product"
                      style={{ width: '12%' }}
                      sortable
                    />
                    <Column
                      body={statusBodyTemplate}
                      header="Status"
                      style={{ width: '10%' }}
                    />
                    <Column
                      body={daysLapsedBodyTemplate}
                      header="Days Lapsed"
                      style={{ width: '10%' }}
                      sortable
                      sortField="daysLapsed"
                    />
                    <Column
                      body={premiumBodyTemplate}
                      header="Premium Lost"
                      style={{ width: '12%', textAlign: 'right' }}
                      sortable
                      sortField="premiumLost"
                    />
                    <Column
                      field="lapseReason"
                      header="Reason"
                      style={{ width: '12%' }}
                    />
                    <Column
                      body={winBackStatusBodyTemplate}
                      header="Win-back"
                      style={{ width: '10%' }}
                    />
                    <Column
                      body={attemptsBodyTemplate}
                      header="Attempts"
                      style={{ width: '8%' }}
                    />
                    <Column
                      body={actionBodyTemplate}
                      header="Actions"
                      style={{ width: '10%' }}
                    />
                  </DataTable>
                </Card>
              </div>
            </TabPanel>

            <TabPanel header="Win-back Campaigns">
              <div className="campaigns-section">
                <Card>
                  <div className="campaigns-header">
                    <h3>Active Win-back Campaigns</h3>
                    <Button
                      label="New Campaign"
                      icon="pi pi-plus"
                      onClick={handleCreateCampaign}
                    />
                  </div>

                  <div className="campaigns-grid">
                    {winBackCampaigns.map(campaign => (
                      <Card key={campaign.campaignId} className="campaign-card">
                        <div className="campaign-content">
                          <div className="campaign-header">
                            <h4>{campaign.campaignName}</h4>
                            <Badge value={campaign.campaignId} />
                          </div>

                          <div className="campaign-details">
                            <div className="detail-item">
                              <label>Period:</label>
                              <span>
                                {new Date(campaign.startDate).toLocaleDateString()} -
                                {new Date(campaign.endDate).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="detail-item">
                              <label>Target:</label>
                              <span>{campaign.targetSegment}</span>
                            </div>
                            <div className="detail-item">
                              <label>Discount:</label>
                              <span>{campaign.offer?.discount}%</span>
                            </div>
                          </div>

                          <div className="campaign-stats">
                            <div className="stat-item">
                              <span className="stat-value">{campaign.statistics?.targetedPolicies || 0}</span>
                              <span className="stat-label">Targeted</span>
                            </div>
                            <div className="stat-item">
                              <span className="stat-value">{campaign.statistics?.converted || 0}</span>
                              <span className="stat-label">Converted</span>
                            </div>
                            <div className="stat-item">
                              <span className="stat-value">{campaign.statistics?.conversionRate || 0}%</span>
                              <span className="stat-label">Rate</span>
                            </div>
                            <div className="stat-item">
                              <span className="stat-value">{formatCurrency(campaign.statistics?.revenueRecovered || 0)}</span>
                              <span className="stat-label">Recovered</span>
                            </div>
                          </div>

                          <div className="campaign-progress">
                            <ProgressBar
                              value={campaign.statistics?.conversionRate || 0}
                              style={{ height: '6px' }}
                            />
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </Card>
              </div>
            </TabPanel>
          </TabView>
        </div>

        {/* Policy Details Dialog */}
        <Dialog
          header="Policy Details"
          visible={detailsVisible}
          onHide={() => setDetailsVisible(false)}
          style={{ width: '70vw' }}
        >
          {selectedPolicy && (
            <TabView>
              <TabPanel header="Policy Information">
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Policy Number:</label>
                    <span>{selectedPolicy.policyNumber}</span>
                  </div>
                  <div className="detail-item">
                    <label>Insured Name:</label>
                    <span>{selectedPolicy.insuredName}</span>
                  </div>
                  <div className="detail-item">
                    <label>Product:</label>
                    <span>{selectedPolicy.product}</span>
                  </div>
                  <div className="detail-item">
                    <label>Premium:</label>
                    <span>{formatCurrency(selectedPolicy.premiumLost || selectedPolicy.currentPremium)}</span>
                  </div>
                  {selectedPolicy.lapseDate && (
                    <div className="detail-item">
                      <label>Lapse Date:</label>
                      <span>{new Date(selectedPolicy.lapseDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  {selectedPolicy.lapseReason && (
                    <div className="detail-item">
                      <label>Lapse Reason:</label>
                      <span>{selectedPolicy.lapseReason}</span>
                    </div>
                  )}
                </div>
              </TabPanel>

              <TabPanel header="Win-back History">
                <div className="winback-history">
                  {selectedPolicy.winBackAttempts?.length > 0 ? (
                    selectedPolicy.winBackAttempts.map((attempt, index) => (
                      <div key={index} className="winback-item">
                        <div className="winback-header">
                          <strong>{attempt.method}</strong>
                          <span>{new Date(attempt.date).toLocaleDateString()}</span>
                        </div>
                        <div className="winback-details">
                          <p>Offer: {attempt.offer}</p>
                          <p>Response: {attempt.response}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p>No win-back attempts made yet.</p>
                  )}
                </div>
              </TabPanel>
            </TabView>
          )}
        </Dialog>

        {/* Win-back Offer Dialog */}
        <Dialog
          header="Send Win-back Offer"
          visible={winBackVisible}
          onHide={() => setWinBackVisible(false)}
          style={{ width: '600px' }}
          footer={winBackDialogFooter}
        >
          {selectedPolicy && (
            <div className="winback-form">
              <div className="policy-info">
                <h4>Policy Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Policy:</label>
                    <span>{selectedPolicy.policyNumber}</span>
                  </div>
                  <div className="info-item">
                    <label>Insured:</label>
                    <span>{selectedPolicy.insuredName}</span>
                  </div>
                  <div className="info-item">
                    <label>Premium:</label>
                    <span>{formatCurrency(selectedPolicy.premiumLost || selectedPolicy.currentPremium)}</span>
                  </div>
                </div>
              </div>

              <div className="offer-details">
                <h4>Win-back Offer</h4>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Discount (%)</label>
                    <InputNumber
                      value={winBackOffer.discount}
                      onValueChange={(e) => setWinBackOffer({...winBackOffer, discount: e.value})}
                      min={0}
                      max={50}
                      suffix="%"
                    />
                  </div>

                  <div className="form-field">
                    <label>Payment Terms</label>
                    <Dropdown
                      value={winBackOffer.paymentTerms}
                      onChange={(e) => setWinBackOffer({...winBackOffer, paymentTerms: e.value})}
                      options={paymentTermsOptions}
                    />
                  </div>

                  <div className="form-field">
                    <label>Valid Until</label>
                    <Calendar
                      value={winBackOffer.validUntil}
                      onChange={(e) => setWinBackOffer({...winBackOffer, validUntil: e.value})}
                      dateFormat="mm/dd/yy"
                      minDate={new Date()}
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label>Additional Benefits</label>
                  <div className="benefits-selection">
                    {benefitOptions.map(benefit => (
                      <div key={benefit} className="benefit-checkbox">
                        <input
                          type="checkbox"
                          id={benefit}
                          checked={winBackOffer.additionalBenefits.includes(benefit)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setWinBackOffer({
                                ...winBackOffer,
                                additionalBenefits: [...winBackOffer.additionalBenefits, benefit]
                              });
                            } else {
                              setWinBackOffer({
                                ...winBackOffer,
                                additionalBenefits: winBackOffer.additionalBenefits.filter(b => b !== benefit)
                              });
                            }
                          }}
                        />
                        <label htmlFor={benefit}>{benefit}</label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-field">
                  <label>Personal Message</label>
                  <InputTextarea
                    value={winBackOffer.message}
                    onChange={(e) => setWinBackOffer({...winBackOffer, message: e.target.value})}
                    rows={3}
                    placeholder="Add a personal message to the offer"
                  />
                </div>
              </div>
            </div>
          )}
        </Dialog>

        {/* Create Campaign Dialog */}
        <Dialog
          header="Create Win-back Campaign"
          visible={campaignVisible}
          onHide={() => setCampaignVisible(false)}
          style={{ width: '700px' }}
          footer={campaignDialogFooter}
        >
          <div className="campaign-form">
            <div className="form-grid">
              <div className="form-field">
                <label>Campaign Name</label>
                <InputText
                  value={newCampaign.name}
                  onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                  placeholder="Enter campaign name"
                />
              </div>

              <div className="form-field">
                <label>Target Segment</label>
                <Dropdown
                  value={newCampaign.targetSegment}
                  onChange={(e) => setNewCampaign({...newCampaign, targetSegment: e.value})}
                  options={targetSegmentOptions}
                />
              </div>

              <div className="form-field">
                <label>Start Date</label>
                <Calendar
                  value={newCampaign.startDate}
                  onChange={(e) => setNewCampaign({...newCampaign, startDate: e.value})}
                  dateFormat="mm/dd/yy"
                  minDate={new Date()}
                />
              </div>

              <div className="form-field">
                <label>End Date</label>
                <Calendar
                  value={newCampaign.endDate}
                  onChange={(e) => setNewCampaign({...newCampaign, endDate: e.value})}
                  dateFormat="mm/dd/yy"
                  minDate={newCampaign.startDate}
                />
              </div>

              <div className="form-field">
                <label>Discount (%)</label>
                <InputNumber
                  value={newCampaign.discount}
                  onValueChange={(e) => setNewCampaign({...newCampaign, discount: e.value})}
                  min={0}
                  max={50}
                  suffix="%"
                />
              </div>

              <div className="form-field">
                <label>Budget</label>
                <InputNumber
                  value={newCampaign.budget}
                  onValueChange={(e) => setNewCampaign({...newCampaign, budget: e.value})}
                  mode="currency"
                  currency="PHP"
                  locale="en-PH"
                />
              </div>
            </div>

            <div className="form-field">
              <label>Campaign Benefits</label>
              <div className="benefits-selection">
                {benefitOptions.map(benefit => (
                  <div key={benefit} className="benefit-checkbox">
                    <input
                      type="checkbox"
                      id={`campaign-${benefit}`}
                      checked={newCampaign.benefits.includes(benefit)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewCampaign({
                            ...newCampaign,
                            benefits: [...newCampaign.benefits, benefit]
                          });
                        } else {
                          setNewCampaign({
                            ...newCampaign,
                            benefits: newCampaign.benefits.filter(b => b !== benefit)
                          });
                        }
                      }}
                    />
                    <label htmlFor={`campaign-${benefit}`}>{benefit}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Dialog>
      </div>
    </div>
  );
};

export default LapseManagement;